import { FunctionComponent } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import { useTranslation } from 'react-i18next'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './confirmActiveNotificationAccountDialog.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Check } from '@mui/icons-material'
import { useMutation } from '@apollo/client'
import { ACTIVATE_ACCOUNT_MAIL_NOTIFICATION } from '@/modules/API/software/emailHandler'
import { EmailUser } from '@/types/EmailHandlerType'
import EmailData from '../../emailData'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface ConfirmActiveAccountProps {
    open: boolean
    handleClose: () => void
    email: string
    setEmail: (email: string) => void
    emailActivationData?: EmailUser
    setEmailActivationData: (data: EmailUser | undefined) => void
}

const ConfirmActiveNotificationAccountDialog: FunctionComponent<ConfirmActiveAccountProps> = ({
    open,
    handleClose,
    email,
    setEmail,
    emailActivationData,
    setEmailActivationData
}: ConfirmActiveAccountProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [activateAccountNotification] = useMutation(ACTIVATE_ACCOUNT_MAIL_NOTIFICATION, {
        onCompleted: () => {
            showNotificationSuccess(t('activeAccountNotificationSuccess'))

            setEmail('')
            setEmailActivationData(undefined)

            handleClose()
        },
        onError: (error) => {
            if (error.message.split('Message: ')[0] === 'Notification already enabled') {
                showNotificationError(t('notificationAlreadyActivated'))
            } else {
                showNotificationError(t('activeAccountNotificationError'))
            }
            handleClose()
        }
    })

    function handleActivateAccountNotification(push: boolean): void {
        activateAccountNotification({
            variables: {
                email: email,
                push: push
            }
        })
    }

    return (
        <Dialog open={open} handleClose={handleClose} className={styles.Dialog}>
            <DialogHeader title={t('activateAccountNotification')} />
            <DialogBody className={styles.DialogBody}>
                <p className={styles.Text}>{t('activateAccountNotificationMessage')}</p>
                <div className={styles.Text}>
                    <span className={styles.TextEmail}> {email} </span>
                    <EmailData data={emailActivationData} loading={false} />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('pushNotification')}
                        customIcon={<Check />}
                        onClick={() => handleActivateAccountNotification(true)}
                        className={styles.Button}
                    />
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('emailNotification')}
                        customIcon={<Check />}
                        onClick={() => handleActivateAccountNotification(false)}
                        className={styles.Button}
                    />
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default ConfirmActiveNotificationAccountDialog
